import React from 'react';
import { Box, type SxProps, type Theme } from '@mui/material';

export interface BlurProps {
  /** Whether to apply the blur effect */
  active?: boolean;
  /** Blur intensity in pixels (default: 8) */
  intensity?: number;
  /** Brightness reduction (0-1, default: 0.5) */
  brightness?: number;
  /** Whether to disable pointer events when blurred (default: true) */
  disableInteraction?: boolean;
  /** Content to blur */
  children: React.ReactNode;
  /** Additional sx props */
  sx?: SxProps<Theme>;
}

/**
 * Blur - Atomic component for applying blur effect to content
 * 
 * Use cases:
 * - Compliance overlays (sign-in required, terms of use)
 * - Premium content gating
 * - Loading states
 * 
 * @example
 * ```tsx
 * <Blur active={requiresSignIn}>
 *   <img src={listingImage} />
 * </Blur>
 * ```
 */
export function Blur({
  active = false,
  intensity = 8,
  brightness = 0.5,
  disableInteraction = true,
  children,
  sx,
}: BlurProps) {
  return (
    <Box
      data-blur-active={active}
      sx={{
        transition: 'filter 0.2s ease-in-out',
        ...(active && {
          filter: `blur(${intensity}px) brightness(${brightness * 100}%)`,
          ...(disableInteraction && {
            pointerEvents: 'none',
          }),
        }),
        ...sx,
      }}
    >
      {children}
    </Box>
  );
}

export default Blur;
